import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  FileCopy as FileCopyIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useNotification } from '../../contexts/NotificationContext';
import historiaService from '../../services/historiaService';
import especialidadService from '../../services/especialidadService';
import { useAuth } from '../../hooks/useAuth';
import PageHeader from '../../components/common/PageHeader';
import { formatDate } from '../../utils/dateUtils';
import especialistaService from '../../services/especialistaService';  


const HistoriasList = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [histories, setHistories] = useState([]);
  const [totalHistories, setTotalHistories] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    especialidad_id: '',
    especialista_id: '',
    estado: ''
  });
  const [especialidades, setEspecialidades] = useState([]);
  const [especialistas, setEspecialistas] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const estadosHistoria = [
    { value: 'Activo', label: 'Activo', color: 'success' },
    { value: 'Finalizado', label: 'Finalizado', color: 'primary' },
    { value: 'En pausa', label: 'En pausa', color: 'warning' }
  ];

  // Cargar historias clínicas
  useEffect(() => {
    const fetchHistories = async () => {
      setLoading(true);
      try {
        // Construir parámetros de consulta
        const params = {
          page: page + 1,
          limit: rowsPerPage
        };

        // Agregar búsqueda si existe
        if (search.trim()) {
          params.search = search.trim();
        }

        // Agregar filtros solo si tienen valor
        if (filters.especialidad_id) {
          params.especialidad_id = filters.especialidad_id;
        }
        
        if (filters.especialista_id) {  // 🔧 NUEVO
          params.especialista_id = filters.especialista_id;
        }

        if (filters.estado) {
          params.estado = filters.estado;
        }
        
         
        const response = await historiaService.getHistorias(params);
        setHistories(response.data.historias);
        setTotalHistories(response.data.total);
      } catch (error) {
        console.error('Error fetching histories:', error);
        showNotification('Error al cargar las historias clínicas', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchHistories();
  }, [page, rowsPerPage, search, filters, refreshKey]);

  // Cargar especialidades
  useEffect(() => {
  const fetchData = async () => {
    try {
      // 🔧 AGREGAR CARGA DE ESPECIALISTAS
      const [especialidadesResponse, especialistasResponse] = await Promise.all([
        especialidadService.getEspecialidades(),
        especialistaService.getEspecialistas()  // 🔧 NUEVO
      ]);
      
      setEspecialidades(especialidadesResponse.data);
      setEspecialistas(especialistasResponse.data || []);  // 🔧 NUEVO
    } catch (error) {
      console.error('Error fetching especialidades:', error);
      showNotification('Error al cargar especialidades', 'error');
    }
  };

  fetchData();
}, []);

  // Cambiar de página
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Cambiar número de filas por página
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Actualizar búsqueda
  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  // Actualizar filtros
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  // Limpiar filtros
 const handleClearFilters = () => {
  setFilters({
    especialidad_id: '',
    especialista_id: '',  // 🔧 AGREGAR
    estado: ''
  });
  setSearch('');
  setPage(0);
};

  // Navegar a crear historia clínica
  const handleCreateHistoria = () => {
    // Usualmente se navega primero a seleccionar paciente
    navigate('/pacientes');
  };

  // Obtener color del chip según estado
  const getStatusColor = (estado) => {
    const statusInfo = estadosHistoria.find(s => s.value === estado);
    return statusInfo ? statusInfo.color : 'default';
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader
        title="Historias Clínicas"
        breadcrumbs={[
          { text: 'Dashboard', to: '/dashboard' },
          { text: 'Historias Clínicas' }
        ]}
        actionButton={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateHistoria}
          >
            Nueva Historia Clínica
          </Button>
        }
      />

      <Card>
        <CardContent>
          {/* Filtros */}
          <Box mb={3}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Buscar por nombre del paciente, diagnóstico o CIE-10..."
                  value={search}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="especialidad-label">Especialidad</InputLabel>
                  <Select
                    labelId="especialidad-label"
                    value={filters.especialidad_id}
                    onChange={(e) => handleFilterChange('especialidad_id', e.target.value)}
                    label="Especialidad"
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {especialidades.map(especialidad => (
                      <MenuItem key={especialidad.id} value={especialidad.id}>
                        {especialidad.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="especialista-label">Especialista</InputLabel>
                  <Select
                    labelId="especialista-label"
                    value={filters.especialista_id || ''}
                    onChange={(e) => handleFilterChange('especialista_id', e.target.value)}
                    label="Especialista"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {especialistas.map(especialista => (
                      <MenuItem key={especialista.id} value={especialista.id}>
                        {especialista.usuario?.nombre} {especialista.usuario?.apellidos}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="estado-label">Estado</InputLabel>
                  <Select
                    labelId="estado-label"
                    value={filters.estado}
                    onChange={(e) => handleFilterChange('estado', e.target.value)}
                    label="Estado"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {estadosHistoria.map(estado => (
                      <MenuItem key={estado.value} value={estado.value}>
                        {estado.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end">
                  <Button 
                    variant="outlined" 
                    onClick={handleClearFilters}
                    sx={{ mr: 1 }}
                  >
                    Limpiar Filtros
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Tabla de historias clínicas */}
          {loading ? (
            <Box display="flex" justifyContent="center" my={5}>
              <CircularProgress />
            </Box>
          ) : histories.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography variant="body1" color="textSecondary">
                No se encontraron historias clínicas{search || Object.values(filters).some(v => v) ? ' con los filtros aplicados.' : '.'}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleCreateHistoria}
                sx={{ mt: 2 }}
              >
                Crear Nueva Historia Clínica
              </Button>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>N° Historia</TableCell>
                      <TableCell>Paciente</TableCell>
                      <TableCell>Especialidad</TableCell>
                      <TableCell>Fecha Creación</TableCell>
                      <TableCell>Diagnóstico CIE-10</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {histories.map((historia) => (
                      <TableRow key={historia.id}>
                        <TableCell>{historia.paciente.numero_historia}</TableCell>
                        <TableCell>
                          <RouterLink to={`/pacientes/${historia.paciente_id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', '&:hover': { textDecoration: 'underline', color: 'primary.main' } }}>
                              {historia.paciente.nombres} {historia.paciente.apellidos}
                            </Typography>
                          </RouterLink>
                        </TableCell>
                        <TableCell>{historia.especialidad.nombre}</TableCell>
                        <TableCell>{formatDate(historia.fecha_creacion)}</TableCell>
                        <TableCell>{historia.diagnostico_cie10 || '-'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={historia.estado} 
                            size="small"
                            color={getStatusColor(historia.estado)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Tooltip title="Ver HCE">
                              <IconButton
                                size="small"
                                color="info"
                                component={RouterLink}
                                to={`/hce/${historia.paciente_id}`}
                                sx={{ mr: 1 }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                color="primary"
                                component={RouterLink}
                                to={`/hce/${historia.paciente_id}`}
                                sx={{ mr: 1 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Imprimir">
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => {
                                  showNotification('Funcionalidad de impresión en desarrollo', 'info');
                                }}
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalHistories}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                labelRowsPerPage="Filas por página:"
              />
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default HistoriasList;